import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type ListDropdownItem } from '@sr/types';
import { isFromPoland } from '@sr/utils';
import { type BadgeVariant } from './components/styles';
import { type LocationListItemsType } from './types';
import { LocationCountTemplate } from './utils';

const countLocations = (item: LocationListItemsType, hideInactiveLocations: boolean): number => {
  const countsSelf = !!item.meta?.isLocation && !(hideInactiveLocations && !item.meta.isActive);

  return item.children.reduce(
    (count, child) => count + countLocations(child, hideInactiveLocations),
    countsSelf ? 1 : 0,
  );
};

const countChildLocations = (item: LocationListItemsType, hideInactiveLocations: boolean): number =>
  item.children.reduce((count, child) => count + countLocations(child, hideInactiveLocations), 0);

type LocationDescription = {
  description: string;
  /** Child location count shown on its own line, when the description has no place for it. */
  locationCount?: string;
};

export const getLocationDescription = ({
  item,
  hideInactiveLocations,
  showLocationCount,
  locationLabel,
  locationsLabel,
}: {
  item: LocationListItemsType;
  hideInactiveLocations: boolean;
  showLocationCount: boolean;
  locationLabel: string;
  locationsLabel: string;
}): LocationDescription => {
  const rawDescription = item.meta?.description ?? '';
  const childLocationCount = countChildLocations(item, hideInactiveLocations);
  const label = childLocationCount > 1 ? locationsLabel : locationLabel;
  const countText = `+${childLocationCount} ${label}`;
  const hasCountInDescription = !!rawDescription && rawDescription.includes(LocationCountTemplate);

  if (!showLocationCount || childLocationCount === 0) {
    return { description: rawDescription.replace(LocationCountTemplate, '') };
  }

  if (hasCountInDescription) {
    return { description: rawDescription.replace(LocationCountTemplate, countText) };
  }

  return { description: rawDescription, locationCount: countText };
};

export const isLocationVisible = (
  item: LocationListItemsType,
  hideInactiveLocations: boolean,
): boolean =>
  !hideInactiveLocations ||
  !!item.meta?.activeChildren ||
  (!!item.meta?.isActive && !item.meta.isUnselectable);

type LocationBadge = { variant: BadgeVariant; name: string };

const badgeByType: Record<string, LocationBadge> = {
  csrdEnterprise: { variant: 'ghost', name: 'Enterprise' },
  csrdPremium: { variant: 'secondary', name: 'Premium' },
};

const getLocationBadge = (item: LocationListItemsType): LocationBadge | undefined => {
  const badgeType = item.meta?.badge;

  if (!badgeType) return undefined;

  return badgeByType[badgeType] ?? { variant: 'primary', name: 'Standard' };
};

export const showStatusLabelOutsidePoland = (item: ListDropdownItem) => !isFromPoland(item.value);

/** What a location row shows besides its label. */
export const useLocationRow = ({
  item,
  hideInactiveLocations,
  multiSelect,
  showStatusLabel,
}: {
  item: LocationListItemsType;
  hideInactiveLocations: boolean;
  multiSelect: boolean;
  showStatusLabel: boolean;
}) => {
  const { t } = useTranslation();
  const { description, locationCount } = useMemo(
    () =>
      getLocationDescription({
        item,
        hideInactiveLocations,
        showLocationCount: multiSelect,
        locationLabel: t('components.dropdown.location'),
        locationsLabel: t('components.dropdown.locations'),
      }),
    [hideInactiveLocations, item, multiSelect, t],
  );

  return {
    description,
    locationCount,
    badge: getLocationBadge(item),
    showStatus: !!item.meta?.isLocation && multiSelect && showStatusLabel,
  };
};
